// src/components/EasterEggs/HeartEasterEgg.jsx
import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Heart } from 'lucide-react';

// Animation for flying hearts
const flyAnimation = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg) scale(0);
    opacity: 0;
  }
  15% {
    transform: translate(10px, -20px) rotate(5deg) scale(0.6);
    opacity: 0.6;
  }
  30% {
    transform: translate(20px, -40px) rotate(10deg) scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: translate(30px, -80px) rotate(15deg) scale(1);
    opacity: 1;
  }
  70% {
    transform: translate(40px, -120px) rotate(20deg) scale(0.9);
    opacity: 0.9;
  }
  85% {
    transform: translate(50px, -160px) rotate(25deg) scale(0.7);
    opacity: 0.7;
  }
  100% {
    transform: translate(60px, -200px) rotate(30deg) scale(0.3);
    opacity: 0;
  }
`;

// Animation for falling hearts
const fallAnimation = keyframes`
  0% {
    transform: translate(0, -200px) rotate(0deg) scale(0);
    opacity: 0;
  }
  15% {
    transform: translate(-10px, -160px) rotate(-5deg) scale(0.6);
    opacity: 0.6;
  }
  30% {
    transform: translate(-20px, -120px) rotate(-10deg) scale(0.8);
    opacity: 0.8;
  }
  50% {
    transform: translate(-30px, -80px) rotate(-15deg) scale(1);
    opacity: 1;
  }
  70% {
    transform: translate(-40px, -40px) rotate(-20deg) scale(0.9);
    opacity: 0.9;
  }
  85% {
    transform: translate(-50px, -20px) rotate(-25deg) scale(0.7);
    opacity: 0.7;
  }
  100% {
    transform: translate(-60px, 0) rotate(-30deg) scale(0.3);
    opacity: 0;
  }
`;

const FlyingHeart = styled.div`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  z-index: 2000;
  pointer-events: none;
  
  svg {
    color: ${props => props.$color || '#ef4444'};
    filter: drop-shadow(0 0 5px rgba(239, 68, 68, 0.5));
    animation: ${props => (props.$direction === 'right' ? flyAnimation : fallAnimation)} ${props => props.$duration}s linear forwards;
    fill: ${props => props.$color || '#ef4444'};
  }
`;

const HeartContainer = styled.span`
  display: inline-flex;
  cursor: pointer;
  position: relative;
  
  svg {
    transition: transform 0.3s ease, color 0.3s ease;
  }
  
  &:hover svg {
    transform: scale(1.2);
  }
  
  &:active svg {
    transform: scale(0.9);
  }
`;

const HeartEasterEgg = () => {
  const [clickCount, setClickCount] = useState(0);
  const [showHearts, setShowHearts] = useState(false);
  const [hearts, setHearts] = useState([]);
  
  // Sound reference
  const soundRef = useRef(null);
  
  // Sound effect 
  const playKissSound = () => {
    // Create the audio element if it doesn't exist
    if (!soundRef.current) {
      soundRef.current = new Audio('/sounds/kiss.mp3');
      soundRef.current.volume = 0.5;
    }
    
    // Reset the audio position to start (in case it was played before)
    soundRef.current.currentTime = 0;
    soundRef.current.play().catch(e => console.log("Audio play error: ", e));
  };
  
  const handleHeartClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      // Trigger Easter egg
      playKissSound();
      setShowHearts(true);
      
      // Reset after animation completes
      setTimeout(() => {
        setShowHearts(false);
        setClickCount(0);
      }, 5000);
    }
  };
  
  // Generate flying hearts
  useEffect(() => {
    if (showHearts) {
      const numHearts = 50;
      const newHearts = [];
      
      for (let i = 0; i < numHearts; i++) {
        newHearts.push({
          id: i,
          top: Math.random() * window.innerHeight,
          left: Math.random() * window.innerWidth,
          size: Math.floor(Math.random() * 24) + 12, // 12-36px
          duration: Math.random() * 3 + 2, // 2-5 seconds
          delay: Math.random() * 2,
          direction: Math.random() > 0.5 ? 'right' : 'left',
          color: i % 5 === 0 ? '#ff9999' : i % 7 === 0 ? '#ff66b2' : '#ef4444'
        });
      }
      
      setHearts(newHearts);
    } else {
      setHearts([]);
    }
  }, [showHearts]);
  
  return (
    <>
      <HeartContainer onClick={handleHeartClick}>
        <Heart 
          size={14} 
          fill={clickCount > 0 ? "#ef4444" : "none"} 
          color={clickCount > 0 ? "#ef4444" : "currentColor"} 
        />
      </HeartContainer>
      
      {hearts.map(heart => (
        <FlyingHeart
          key={heart.id}
          $top={heart.top}
          $left={heart.left}
          $duration={heart.duration}
          $direction={heart.direction}
          $color={heart.color}
          style={{ animationDelay: `${heart.delay}s` }}
        >
          <Heart size={heart.size} fill={heart.color} />
        </FlyingHeart>
      ))}
    </>
  );
};

export default HeartEasterEgg;